import { aiModelsFixture } from "./llm-data";
import {
  libraryFixtures,
  initialFolders,
  bodyFingerprint,
  descendantIds,
  type LibraryItem as FixtureItem,
  type LibraryFolder,
} from "./manuscript-library-data";
import { customers } from "./console-model";
import { previewManuscript } from "./manuscript";
import type {
  LibraryAdapter,
  LibraryItem,
  FileRef,
  LibraryDetail,
  ManuscriptCheck,
  FolderResult,
} from "./library-adapter";
export type FixtureState = { items: FixtureItem[]; folders: LibraryFolder[] };
const fixtures = new WeakSet<LibraryAdapter>();
const video = (name: string) => /\.(mp4|mov|m4v|avi|mkv|webm|3gp)$/i.test(name);
export function fixtureScenario(
  adapter: LibraryAdapter,
  example?: string,
): LibraryAdapter {
  if (example !== "conflict" || !fixtures.has(adapter)) return adapter;
  return {
    ...adapter,
    async revise() {
      throw new Error(
        "다른 관리자이 먼저 수정했습니다 (409). 내 초안은 유지됩니다. 최신 버전을 확인하거나 초안을 복사해 주세요.",
      );
    },
  };
}
export function createFixtureLibraryAdapter(bridge?: {
  read(): FixtureState;
  write(state: FixtureState): void;
}): LibraryAdapter {
  let state: FixtureState = {
    items: libraryFixtures(),
    folders: structuredClone(initialFolders),
  };
  const read = () => bridge?.read() ?? state;
  const write = (next: FixtureState) => {
    state = next;
    bridge?.write(next);
  };
  const refs = new Map<string, FileRef[]>(),
    revisionRefs = new Map<string, FileRef[]>(),
    urls = new Map<string, string>([
      ["onyou:guide.jpg", "/meta-crew-manuscript/sample/morning.jpg"],
      ["hq:guide.jpg", "/meta-crew-manuscript/sample/morning.jpg"],
    ]);
  const names = (files: FileRef[]) => files.map((f) => f.name);
  const fileRefs = (i: FixtureItem) =>
    refs.get(i.id) ??
    i.fileNames.map((name) => ({ fileId: i.customerId + ":" + name, name }));
  const find = (id: string) => {
    const i = read().items.find((x) => x.id === id);
    if (!i) throw new Error("원고를 찾을 수 없습니다.");
    return i;
  };
  const replace = (item: FixtureItem) =>
    write({
      ...read(),
      items: read().items.map((i) => (i.id === item.id ? item : i)),
    });
  const row = (i: FixtureItem): LibraryItem => ({
    ownerCustomerId: i.ownerCustomerId,
    targetCustomerId: i.targetCustomerId,
    readOnly: i.readOnly,
    id: i.id,
    customerId: i.customerId,
    folderId: i.folderId,
    title: i.title,
    status: i.status,
    revision: i.revision,
    valid: i.valid,
    errors: i.errors,
    warnings: i.warnings,
    locked: i.locked,
    fileCount: i.fileNames.length,
    videos: i.fileNames.filter(video).length,
    keywords: i.keywords,
    memo: i.memo,
    source: i.source,
    jobs: i.jobs,
    postedAt: i.postedAt,
    postUrl: i.postUrl,
    lastJobId: i.uses[0]?.jobId,
    lastRunStatus: i.uses[0]?.runStatus,
  });
  const detail = (i: FixtureItem): LibraryDetail => ({
    ...row(i),
    body: i.body,
    files: fileRefs(i),
    revisions: i.revisions.map(({ revision, createdAt, valid }) => ({
      revision,
      createdAt,
      valid,
      createdByName: "운영팀",
    })),
    uses: i.uses,
    sameBody: read()
      .items.filter(
        (x) =>
          x.id !== i.id &&
          x.targetCustomerId === i.targetCustomerId &&
          bodyFingerprint(x.body) === bodyFingerprint(i.body),
      )
      .map((x) => ({
        id: x.id,
        title: x.title,
        customerName:
          customers.find((c) => c.id === x.customerId)?.name ?? x.customerId,
        status: x.status,
      })),
  });
  const check = (body: string, files: FileRef[]): ManuscriptCheck => {
    const p = previewManuscript(body, names(files));
    return {
      title: p.title,
      valid: !p.errors.length,
      errors: p.errors,
      warnings: [],
      settings: {
        tags: p.tags,
        visibility:
          p.visibility === "비공개"
            ? "private"
            : p.visibility === "이웃"
              ? "neighbors"
              : "public",
        category: p.category,
      },
      media: p.media.map((name) => ({
        name,
        video: video(name),
        fileId:
          files.find((f) => f.name.toLowerCase() === name.toLowerCase())
            ?.fileId ?? null,
      })),
      blocks: p.blocks.map((b) =>
        b.startsWith("![")
          ? {
              type: "media",
              files: [...b.matchAll(/!\[([^\]]*)\]\(([^)]+)\)/g)].map((m) => ({
                name: m[2],
                caption: m[1],
              })),
            }
          : b.startsWith("## ")
            ? { type: "heading", text: b.slice(3) }
            : { type: "paragraph", text: b.replace(/^> /, "") },
      ),
    };
  };
  const assertFolder = (customer: string, id: string | null) => {
    if (
      id &&
      !read().folders.some((f) => f.id === id && f.customerId === customer)
    )
      throw new Error("다른 고객사 폴더를 사용할 수 없습니다.");
  };
  const adapter: LibraryAdapter = {
    customers: customers.map((c) => ({
      id: c.id,
      name: c.name,
      status: c.state,
    })),
    async folders(customerId) {
      const count = (items: FixtureItem[]) =>
        Object.fromEntries(
          ["draft", "ready", "scheduled", "published", "archived"].map((s) => [
            s,
            items.filter((i) => i.status === s).length,
          ]),
        );
      const all = read().items.filter((i) => i.customerId === customerId);
      return {
        folders: read()
          .folders.filter((f) => f.customerId === customerId)
          .map((f) => ({
            ...f,
            counts: count(all.filter((i) => i.folderId === f.id)),
          })),
        unfiled: count(all.filter((i) => !i.folderId)),
      } as FolderResult;
    },
    async list(q) {
      const items = read().items.filter(
        (i) =>
          i.customerId === q.customerId &&
          (q.status ? i.status === q.status : i.status !== "archived") &&
          (!q.q || (i.title + i.body).includes(q.q)) &&
          (q.folderId === "all" ||
            (q.folderId === "none"
              ? !i.folderId
              : (q.deep
                  ? descendantIds(read().folders, q.folderId)
                  : [q.folderId]
                ).includes(i.folderId ?? ""))),
      );
      return {
        items: items.slice(q.offset, q.offset + q.limit).map(row),
        total: items.length,
      };
    },
    async detail(id) {
      return structuredClone(detail(find(id)));
    },
    async revision(id, rev) {
      const i = find(id),
        r = i.revisions.find((r) => r.revision === rev);
      if (!r) throw new Error("버전을 찾을 수 없습니다.");
      return {
        body: r.body,
        files:
          revisionRefs.get(id + ":" + rev) ??
          r.files.map((name) => ({ fileId: i.customerId + ":" + name, name })),
      };
    },
    async check(_customer, body, files) {
      return check(body, files);
    },
    async uploadFile(customer, file) {
      const ref = {
        fileId: customer + ":" + crypto.randomUUID(),
        name: file.name,
      };
      urls.set(ref.fileId, URL.createObjectURL(file));
      return ref;
    },
    async fileUrl(_customer, id) {
      return (
        urls.get(id) ??
        "data:image/svg+xml," +
          encodeURIComponent(
            '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#7b879b" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect width="18" height="18" x="3" y="3" rx="2" ry="2"/><circle cx="9" cy="9" r="2"/><path d="m21 15-4-4L7 21"/></svg>',
          )
      );
    },
    async create(input) {
      assertFolder(input.customerId, input.folderId);
      const c = check(input.body, input.files),
        id = crypto.randomUUID();
      const item: FixtureItem = {
        id,
        customerId: input.customerId,
        ...(input.customerId === "hq"
          ? {
              ownerCustomerId: null,
              targetCustomerId:
                read().folders.find((f) => f.id === input.folderId)
                  ?.targetCustomerId ?? null,
              readOnly: false,
            }
          : {}),
        folderId: input.folderId,
        title: c.title || "제목 없는 원고",
        body: input.body,
        fileNames: names(input.files),
        keywords: [],
        memo: "",
        source: "editor",
        status: c.valid ? "ready" : "draft",
        revision: 1,
        valid: c.valid,
        errors: c.errors.length,
        warnings: c.warnings.length,
        locked: false,
        jobs: 0,
        postedAt: null,
        postUrl: null,
        revisions: [
          {
            revision: 1,
            body: input.body,
            files: names(input.files),
            valid: c.valid,
            createdAt: "방금",
          },
        ],
        uses: [],
      };
      refs.set(id, input.files);
      revisionRefs.set(id + ":1", input.files);
      write({ ...read(), items: [item, ...read().items] });
      return row(item);
    },
    async revise(id, input) {
      const i = find(id);
      if (i.readOnly)
        throw Error("고객사가 등록한 원고입니다. 직원은 읽기만 가능합니다.");
      if (i.locked)
        throw new Error("발행 예정·게시된 원고는 고칠 수 없습니다.");
      if (input.revision !== i.revision)
        throw new Error("다른 사람이 먼저 고쳤습니다");
      const changed =
        i.body !== input.body ||
        JSON.stringify(fileRefs(i)) !== JSON.stringify(input.files);
      if (!changed) return { item: row(i), changed: false };
      const c = check(input.body, input.files),
        rev = i.revision + 1;
      const next = {
        ...i,
        body: input.body,
        fileNames: names(input.files),
        title: c.title || "제목 없는 원고",
        revision: rev,
        valid: c.valid,
        errors: c.errors.length,
        warnings: c.warnings.length,
        status: c.valid ? i.status : ("draft" as const),
        revisions: [
          {
            revision: rev,
            body: input.body,
            files: names(input.files),
            valid: c.valid,
            createdAt: "방금",
          },
          ...i.revisions,
        ],
      };
      refs.set(id, input.files);
      revisionRefs.set(id + ":" + rev, input.files);
      replace(next);
      return { item: row(next), changed: true };
    },
    async patch(id, input) {
      const i = find(id);
      if (i.readOnly)
        throw Error("고객사가 등록한 원고입니다. 직원은 읽기만 가능합니다.");
      if (input.folderId !== undefined)
        assertFolder(i.customerId, input.folderId);
      if (input.state && (i.locked || (input.state === "ready" && !i.valid)))
        throw new Error("잠금 또는 형식 오류로 상태를 바꿀 수 없습니다.");
      let status = i.status;
      if (input.state) status = input.state;
      if (input.archived !== undefined)
        status = input.archived
          ? "archived"
          : i.locked
            ? i.postUrl
              ? "published"
              : "scheduled"
            : i.valid
              ? "ready"
              : "draft";
      const next = {
        ...i,
        folderId: input.folderId === undefined ? i.folderId : input.folderId,
        keywords: input.keywords ?? i.keywords,
        memo: input.memo === undefined ? i.memo : (input.memo ?? ""),
        status,
      };
      replace(next);
      return row(next);
    },
    async bulk(ids, action, folderId) {
      const results = [];
      for (const id of ids) {
        try {
          await adapter.patch(
            id,
            action === "move"
              ? { folderId: folderId ?? null }
              : action === "ready" || action === "draft"
                ? { state: action }
                : { archived: action === "archive" },
          );
          results.push({ id, ok: true });
        } catch (e) {
          results.push({ id, ok: false, error: (e as Error).message });
        }
      }
      return results;
    },
    async remove(id) {
      if (find(id).readOnly)
        throw new Error(
          "고객사가 등록한 원고입니다. 직원은 읽기만 가능합니다.",
        );
      if (find(id).jobs)
        throw new Error("사용 이력이 있는 원고는 삭제 대신 보관해 주세요.");
      write({ ...read(), items: read().items.filter((i) => i.id !== id) });
    },
    async createFolder(customerId, parentId, name, targetCustomerId) {
      assertFolder(customerId, parentId);
      name = name.trim();
      if (!name) throw new Error("폴더 이름을 입력해 주세요.");
      if (
        read().folders.some(
          (f) =>
            f.customerId === customerId &&
            f.parentId === parentId &&
            f.name === name,
        )
      )
        throw new Error("같은 위치에 같은 이름의 폴더가 있습니다.");
      write({
        ...read(),
        folders: [
          ...read().folders,
          {
            id: crypto.randomUUID(),
            customerId,
            parentId,
            name,
            targetCustomerId:
              read().folders.find((f) => f.id === parentId)?.targetCustomerId ??
              targetCustomerId ??
              null,
            targetInherited: !!read().folders.find((f) => f.id === parentId)
              ?.targetCustomerId,
          },
        ],
      });
    },
    async updateFolder(id, input) {
      const f = read().folders.find((f) => f.id === id);
      if (!f) throw new Error("폴더를 찾을 수 없습니다.");
      const parent = input.parentId === undefined ? f.parentId : input.parentId,
        name = input.name?.trim() ?? f.name;
      assertFolder(f.customerId, parent);
      if (parent && descendantIds(read().folders, id).includes(parent))
        throw new Error("자기 하위로 폴더를 옮길 수 없습니다.");
      if (!name) throw new Error("폴더 이름을 입력해 주세요.");
      if (
        read().folders.some(
          (x) =>
            x.id !== id &&
            x.customerId === f.customerId &&
            x.parentId === parent &&
            x.name === name,
        )
      )
        throw new Error("같은 위치에 같은 이름의 폴더가 있습니다.");
      write({
        ...read(),
        folders: read().folders.map((x) =>
          x.id === id
            ? {
                ...x,
                name,
                parentId: parent,
                targetCustomerId:
                  input.targetCustomerId === undefined
                    ? x.targetCustomerId
                    : input.targetCustomerId,
              }
            : x,
        ),
      });
    },
    async deleteFolder(id) {
      const f = read().folders.find((f) => f.id === id);
      if (!f) throw new Error("폴더를 찾을 수 없습니다.");
      write({
        items: read().items.map((i) =>
          i.folderId === id ? { ...i, folderId: f.parentId } : i,
        ),
        folders: read()
          .folders.filter((x) => x.id !== id)
          .map((x) => (x.parentId === id ? { ...x, parentId: f.parentId } : x)),
      });
    },
    async batch(customerId, folderId, items) {
      if (items.length > 100)
        throw new Error("한 번에 최대 100편까지 올릴 수 있습니다.");
      const results = [];
      for (const input of items) {
        try {
          let parent = folderId;
          for (const name of (input.path ?? "").split("/").filter(Boolean)) {
            let f = read().folders.find(
              (f) =>
                f.customerId === customerId &&
                f.parentId === parent &&
                f.name === name,
            );
            if (!f) {
              await adapter.createFolder(customerId, parent, name);
              f = read().folders.find(
                (f) =>
                  f.customerId === customerId &&
                  f.parentId === parent &&
                  f.name === name,
              )!;
            }
            parent = f.id;
          }
          const item = await adapter.create({
            customerId,
            folderId: parent,
            body: input.body,
            files: input.files,
          });
          await adapter.patch(item.id, {
            keywords: input.keywords,
            memo: input.memo,
          });
          const checked = await adapter.check(
            customerId,
            input.body,
            input.files,
          );
          results.push({
            path: input.path ?? null,
            ok: true,
            title: item.title,
            valid: item.valid,
            errors: checked.errors,
            sameBody: (await adapter.detail(item.id)).sameBody.length,
          });
        } catch (e) {
          results.push({
            path: input.path ?? null,
            ok: false,
            error: (e as Error).message,
          });
        }
      }
      return results;
    },
    async machines(customer) {
      return [
        {
          id: customer + "-pc",
          label:
            (customers.find((c) => c.id === customer)?.name ?? customer) +
            " PC",
        },
      ];
    },
    async devices(customer, machine) {
      return machine
        ? [{ serial: customer + "-device", label: "블로그 01 · Galaxy S24" }]
        : [];
    },
    async models() {
      return structuredClone(aiModelsFixture);
    },
    async send(input) {
      const owner = find(input.ids[0]).customerId,
        results = [];
      for (const [index, id] of input.ids.entries()) {
        const i = find(id);
        const runAt = input.startAt
          ? new Date(
              new Date(input.startAt).getTime() +
                (input.intervalMinutes ?? 0) * 60000 * index,
            ).toISOString()
          : null;
        let error =
          i.customerId !== owner
            ? "다른 고객사의 원고"
            : i.locked
              ? "이미 예약·게시된 원고"
              : !i.valid
                ? "형식 검사 오류"
                : i.status !== "ready"
                  ? "준비됨 원고가 아님"
                  : "";
        const duplicate = read().items.find(
          (x) =>
            x.id !== id &&
            x.locked &&
            x.targetCustomerId ===
              (input.targetCustomerId ??
                input.targetCustomerIds?.[id] ??
                i.targetCustomerId) &&
            bodyFingerprint(x.body) === bodyFingerprint(i.body),
        );
        if (!error && duplicate)
          error =
            "같은 대상 고객사에 내용이 같은 원고가 있습니다 · " +
            duplicate.title +
            " (" +
            (customers.find((c) => c.id === duplicate.customerId)?.name ??
              duplicate.customerId) +
            ")";
        if (error) {
          results.push({ id, title: i.title, ok: false, error, runAt });
          continue;
        }
        const seoSkipped =
          check(i.body, fileRefs(i)).settings.visibility !== "public"
            ? "비공개·이웃 공개 원고는 SEO 추적에서 제외됩니다."
            : null;
        replace({
          ...i,
          status: "scheduled",
          locked: true,
          jobs: i.jobs + 1,
          uses: [
            {
              jobId: "JOB-" + crypto.randomUUID().slice(0, 6),
              executeCustomerId: input.executeCustomerId ?? owner,
              runStatus: "대기",
              reviewStatus: "미게시",
              postUrl: null,
              keywords: seoSkipped ? [] : (input.keywords[id] ?? i.keywords),
              revision: i.revision,
              runAt: runAt ?? "지금",
            },
            ...i.uses,
          ],
        });
        results.push({ id, title: i.title, ok: true, runAt, seoSkipped });
      }
      return results;
    },
  };
  fixtures.add(adapter);
  return adapter;
}

export type TaskSetupExample = "normal" | "no-pc" | "pc-loading" | "pc-error";
/** Storybook-only connection scenarios; the normal fixture and live adapter stay unchanged. */
export function createTaskSetupFixture(
  example: TaskSetupExample = "normal",
): LibraryAdapter {
  const base = createFixtureLibraryAdapter();
  if (example === "normal") return base;
  return {
    ...base,
    async machines(customerId) {
      if (customerId !== "moru") return base.machines(customerId);
      if (example === "pc-error")
        throw new Error(
          "PC 연결 정보를 불러오지 못했습니다. 잠시 후 다시 시도해 주세요.",
        );
      if (example === "pc-loading")
        return new Promise<Awaited<ReturnType<LibraryAdapter["machines"]>>>(
          () => {},
        );
      return [];
    },
    async devices(customerId, machineId) {
      return customerId === "moru" ? [] : base.devices(customerId, machineId);
    },
  };
}
